import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, LogOut, Globe, Shield, Database, HelpCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchUserProfile } from '../../redux/user/profile/profileActions';
import { logoutUser } from '../../redux/authentication/utils/core/authslice';
import UserAvatar from '../UserAvatar/UserAvatar';
import { Dropdown } from '../ui';

const DropdownMenu = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const dispatch = useDispatch();
  const profile = useSelector((state) => state.profile) || {};
  const user = profile.user;

  // Guarded the same way securitySettingsPage.jsx's already-fixed MFA bug
  // was -- this dropdown lives in GlobalBar, mounted on every authenticated
  // page, so an unconditional dispatch here compounded with every other
  // component independently fetching the same profile (profile.jsx,
  // UnifiedStockerDashboard.jsx) into 7+ near-simultaneous GET /users/me
  // calls on a single page load, confirmed live via network trace. See
  // Technical_Docs/13_FIX_HISTORY.md's Frontend Completeness audit pass.
  useEffect(() => {
    if (!user) dispatch(fetchUserProfile());
  }, [dispatch, user]);

  const closeDropdown = () => setIsOpen(false);

  return (
    <Dropdown
      align="right"
      open={isOpen}
      onOpenChange={setIsOpen}
      panelAriaLabel={t('nav.accountMenu')}
      panelClassName="w-[min(18rem,calc(100vw-2rem))] py-2 transition-all"
      trigger={({ toggle }) => (
        <button
          onClick={toggle}
          aria-label={t('nav.accountMenu')}
          aria-expanded={isOpen}
          aria-haspopup="true"
          className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          <UserAvatar userId={user?.id} username={user?.username} className="w-8 h-8 shadow-md" textClass="text-xs" />
        </button>
      )}
    >
          {/* User Profile */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center space-x-3">
              <UserAvatar userId={user?.id} username={user?.username} className="w-12 h-12 shadow-sm" textClass="text-base" />
              <div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {user?.username || t('common.loading')}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {user?.email || 'loading@example.com'}
                </div>
                <Link
                  to="/profile"
                  className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline"
                  onClick={closeDropdown}
                >
                  {t('nav.manageAccount')}
                </Link>
              </div>
            </div>
          </div>

          {/* Settings Menu */}
          <div className="py-1">
            <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
              {t('nav.settings')}
            </div>

            <NavMenuItem
              icon={<Globe className="w-5 h-5" />}
              text={t('nav.languageEnglish')}
              to="/settings/language"
              onClick={closeDropdown}
            />

            {user?.role === 'admin' && (
              <NavMenuItem
                icon={<Shield className="w-5 h-5" />}
                text={t('sidebar.adminDashboard')}
                to="/admin"
                onClick={closeDropdown}
              />
            )}

            <NavMenuItem
              icon={<Database className="w-5 h-5" />}
              text={t('sidebar.yourData')}
              to="/settings/your-data"
              onClick={closeDropdown}
            />

            <NavMenuItem
              icon={<Settings className="w-5 h-5" />}
              text={t('sidebar.allSettings')}
              to="/settings"
              onClick={closeDropdown}
            />
          </div>

          <div className="border-t border-gray-200 dark:border-gray-800 mt-2 pt-2">
            <NavMenuItem
              icon={<HelpCircle className="w-5 h-5" />}
              text={t('sidebar.helpCenter')}
              to="/settings/help-center"
              onClick={closeDropdown}
            />

            <LogoutButton onClick={closeDropdown} />
          </div>
    </Dropdown>
  );
};

export const NavMenuItem = ({ icon, text, to, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    role="menuitem"
    className="w-full px-4 py-2 flex items-center space-x-3 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400"
  >
    <span className="text-gray-600 dark:text-gray-300">{icon}</span>
    <span className="text-sm">{text}</span>
  </Link>
);

export const LogoutButton = ({ onClick }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login', { replace: true });
    } catch (error) {
      console.warn('[LogoutButton] Logout failed:', error);
      navigate('/login', { replace: true });
    } finally {
      onClick?.();
    }
  };

  return (
    <button
      onClick={handleLogout}
      role="menuitem"
      className="w-full px-4 py-2 flex items-center space-x-3 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400"
    >
      <span className="text-gray-600 dark:text-gray-300">
        <LogOut className="w-5 h-5" />
      </span>
      <span className="text-sm">{t('nav.signOut')}</span>
    </button>
  );
};

export default DropdownMenu;
